<template>
  <div>
    <div v-for="key in keys" :key="key" class="mb-3">
      <div class="pd-Cart_VariantHeader mb-1">{{ key }}</div>
      <ul
        v-if="propertiesForKey(key).length <= 12"
        class="pd-Cart_VariantList mb-n1 variant-option-values"
      >
        <PropertyButton
          v-for="property in propertiesForKey(key)"
          :key="`${property.key}-${property.value}`"
          :identifier="identifier"
          :property="property"
        />
      </ul>
      <div v-else class="row">
        <div class="col-md-16">
          <PropertySelect
            :properties="propertiesForKey(key)"
            :property-key="key"
            :reset_filter_text="translations.reset_filter"
            :please_choose_text="translations.please_choose"
          />
        </div>
      </div>
    </div>
    <button
      v-if="selectedProperties.length"
      @click="emptySelectedProperties"
      class="btn btn-secondary-subtle btn-sm mb-2"
    >
      {{ translations.reset_properties }}
    </button>
  </div>
</template>

<script>
import PropertyButton from "./VariantPickerPropertyButton";
import PropertySelect from "./VariantPickerPropertySelect";
import compareProperties from "../mixins/compareProperties";
import uniqBy from "lodash/uniqBy";
import orderBy from "lodash/orderBy";
import storeTemplate from "../store/modules/variantPicker";

export default {
  name: "VariantPicker",
  components: {
    PropertyButton,
    PropertySelect,
  },
  mixins: [compareProperties],
  props: {
    identifier: {
      type: String,
      default: "",
    },
    variant_properties: {
      default: "",
      type: String,
    },
    translations: {
      required: true,
      type: Object,
    },
    preselectedProperties: {
      required: true,
      type: Array,
    },
    mutateUrl: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    selectedProperties() {
      return this.$store.getters[`variantPicker${this.identifier}/selectedProperties`];
    },
    variants() {
      return this.$store.getters[`variantPicker${this.identifier}/allProductVariants`];
    },
    chosenVariant() {
      return this.$store.getters[`variantPicker${this.identifier}/chosenVariant`];
    },
    keys() {
      const nestedKeys = this.variants.map(variant =>
        variant.properties.map(property => property.key)
      );

      const keys = Array.prototype.concat.apply([], nestedKeys);

      return [...new Set(keys)];
    },
  },
  watch: {
    selectedProperties() {
      this.selectPropertyIfSingleVariantMatching();
      const variant = this.filterChosenVariant();
      this.setVariantUrl(variant);
    },
  },
  created() {
    if (this.$store._modules.root._children[`variantPicker${this.identifier}`]) return;
    this.$store.registerModule(`variantPicker${this.identifier}`, storeTemplate);
  },
  mounted() {
    const variants = JSON.parse(this.variant_properties);

    this.$store.dispatch(`variantPicker${this.identifier}/setProductVariants`, variants);

    if (variants.length === 1) {
      this.$store.dispatch(`variantPicker${this.identifier}/setChosenVariants`, [variants[0].id]);
    } else if (variants.every(variant => variant.properties.length == 0)) {
      this.$store.dispatch(
        `variantPicker${this.identifier}/setChosenVariants`,
        variants.map(variant => variant.id)
      );
    }

    this.preselectedProperties.forEach(property => this.setSelectedProperties(property));
  },
  methods: {
    setSelectedProperties(property) {
      this.$store.dispatch(`variantPicker${this.identifier}/setSelectedProperties`, property);
    },
    emptySelectedProperties() {
      this.$store.dispatch(`variantPicker${this.identifier}/emptySelectedProperties`);
    },
    propertiesForKey(key) {
      const properties = this.variants
        .map(variant => variant.properties.find(property => property.key === key))
        .filter(prop => prop != undefined);

      const sorted_properties = orderBy(properties, ["sort"], ["asc"]);
      return uniqBy(sorted_properties, property => property.value);
    },
    setVariantUrl(variant) {
      if (this.mutateUrl) {
        if (variant) {
          const url = new URL(variant.url);
          history.replaceState(null, null, location.origin + url.pathname + url.search);
        } else {
          history.replaceState(null, null, location.origin + location.pathname);
        }
      }
    },
    filterChosenVariant() {
      const selectedVariants = this.variants.filter(variant => {
        const variantProperties = variant.properties;
        const selectedVariantProperties = variantProperties.map(variantProperty => {
          const isSelected = this.selectedProperties.some(selectedProperty =>
            this.compareProperties(selectedProperty, variantProperty)
          );

          return isSelected;
        });

        return selectedVariantProperties.every(property => property === true);
      });

      this.$store.dispatch(
        `variantPicker${this.identifier}/setChosenVariants`,
        selectedVariants ? selectedVariants.map(variant => variant.id) : null
      );
      return selectedVariants[0];
    },
    selectPropertyIfSingleVariantMatching() {
      const variantsPossible = this.variants.filter(variant =>
        variant.properties.some(variantProperty =>
          this.selectedProperties.some(selectedProperty =>
            this.compareProperties(selectedProperty, variantProperty)
          )
        )
      );

      if (variantsPossible.length === 1) {
        const propertiesToSelect = variantsPossible[0].properties.filter(
          variantProperty =>
            !this.selectedProperties.some(selectedProperty =>
              this.compareProperties(selectedProperty, variantProperty)
            )
        );

        propertiesToSelect.forEach(propertyToSelect => {
          this.setSelectedProperties(propertyToSelect);
        });
      }
    },
  },
};
</script>
